import React, { useEffect, useState } from "react";
import { X, Moon, ShootingStar, Sun, CalendarBlank, Planet, MapPin, ArrowUp } from "@phosphor-icons/react";
import api from "@/lib/api";

function PlanetRow({ p }) {
  return (
    <div data-testid={`planet-${p.name.toLowerCase()}`} className="glass rounded-2xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="w-3 h-3 rounded-full" style={{ background: p.color, boxShadow: `0 0 10px ${p.color}` }} />
          <span className="font-display font-bold text-base tracking-tight">{p.name}</span>
        </div>
        <span className="text-tel text-[8px] px-2 py-1 rounded-full"
          style={{ background: p.visible_now ? "rgba(0,216,255,0.15)" : "rgba(255,255,255,0.06)", color: p.visible_now ? "#7fe6ff" : "rgba(255,255,255,0.45)" }}>
          {p.visible_now ? "Visible now" : p.up ? "Above horizon" : "Below horizon"}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        <div>
          <div className="text-tel text-[8px] text-white/40 mb-0.5">Look</div>
          <div className="font-mono-tel text-[13px] text-white/90 flex items-center gap-1">
            <ArrowUp size={11} weight="bold" style={{ transform: `rotate(${p.azimuth}deg)`, color: "#00D8FF" }} /> {p.direction}
          </div>
        </div>
        <div>
          <div className="text-tel text-[8px] text-white/40 mb-0.5">Altitude</div>
          <div className="font-mono-tel text-[13px] text-white/90">{p.altitude}°</div>
        </div>
        <div>
          <div className="text-tel text-[8px] text-white/40 mb-0.5">Magnitude</div>
          <div className="font-mono-tel text-[13px] text-white/90">{p.magnitude}</div>
        </div>
      </div>
      <div className="text-white/45 text-xs mt-2">In {p.constellation} · {p.event}{!p.naked_eye ? " · telescope only" : ""}</div>
    </div>
  );
}

export default function SkyEvents({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planets, setPlanets] = useState(null);
  const [locLabel, setLocLabel] = useState("Locating…");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/sky/events").then((r) => setData(r.data)).finally(() => setLoading(false));

    const load = (lat, lon, label) => {
      setLocLabel(label);
      api.get("/sky/planets", { params: { lat, lon } }).then((r) => setPlanets(r.data)).catch(() => setPlanets(null));
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => load(pos.coords.latitude, pos.coords.longitude, "Your location"),
        () => load(40.0, -74.0, "Default (mid-north latitude)"),
        { timeout: 8000 }
      );
    } else {
      load(40.0, -74.0, "Default (mid-north latitude)");
    }
  }, [open]);

  if (!open) return null;

  return (
    <div data-testid="sky-events-panel"
      className="fixed right-0 top-0 h-full w-full sm:w-[400px] z-40 glass slide-in-right flex flex-col"
      style={{ borderLeft: "1px solid rgba(0,216,255,0.2)" }}>
      <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center glow-cyan" style={{ background: "rgba(0,216,255,0.12)" }}>
            <ShootingStar size={20} weight="duotone" color="#00D8FF" />
          </div>
          <div>
            <div className="font-display font-bold text-sm tracking-tight">SKY TONIGHT</div>
            <div className="text-tel text-[9px] text-cyan-400/70">{data?.date || "Live sky events"}</div>
          </div>
        </div>
        <button data-testid="sky-events-close" onClick={onClose} className="text-white/50 hover:text-white transition-colors"><X size={22} /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
        {loading || !data ? (
          <div className="text-tel text-xs text-white/40 py-10 text-center">Reading the sky…</div>
        ) : (
          <>
            {/* Live planet positions */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><Planet size={15} weight="duotone" color="#00D8FF" /><span className="text-tel text-[9px] text-white/50">Planets Right Now</span></div>
                <span className="flex items-center gap-1 text-tel text-[8px] text-white/40"><MapPin size={10} weight="fill" /> {locLabel}</span>
              </div>
              {!planets ? (
                <div className="text-white/40 text-sm">Computing planet positions…</div>
              ) : (
                <>
                  {!planets.is_night && (
                    <div className="text-white/45 text-xs mb-3">The Sun is up ({planets.sun_altitude}°) — planets are hard to spot until after dusk.</div>
                  )}
                  <div className="space-y-3">
                    {planets.planets.map((p) => <PlanetRow key={p.name} p={p} />)}
                  </div>
                </>
              )}
            </div>

            {/* Moon */}
            <div>
              <div className="flex items-center gap-2 mb-3"><Moon size={15} weight="duotone" color="#00D8FF" /><span className="text-tel text-[9px] text-white/50">Moon Phase</span></div>
              <div className="flex items-center gap-4 glass rounded-2xl p-4">
                <div className="relative w-12 h-12 rounded-full flex-shrink-0" style={{ background: "#1a1a22", boxShadow: `inset ${(data.moon.illumination - 50) / 2.2}px 0 10px rgba(255,255,255,0.9), 0 0 18px rgba(255,255,255,0.25)` }} />
                <div>
                  <div className="font-display font-bold text-lg tracking-tight">{data.moon.phase_name}</div>
                  <div className="text-white/50 text-sm">{data.moon.illumination}% illuminated · {data.moon.age_days} days old</div>
                </div>
              </div>
            </div>

            {/* Active showers */}
            <div>
              <div className="flex items-center gap-2 mb-3"><ShootingStar size={15} weight="duotone" color="#00D8FF" /><span className="text-tel text-[9px] text-white/50">Active Meteor Showers</span></div>
              {data.active_showers.length === 0 ? (
                <p className="text-white/40 text-sm">No major showers active right now.</p>
              ) : (
                <div className="space-y-3">
                  {data.active_showers.map((s) => (
                    <div key={s.name} data-testid={`shower-${s.name}`} className="glass rounded-2xl p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-display font-bold text-base tracking-tight text-cyan-200">{s.name}</span>
                        <span className="text-tel text-[8px] px-2 py-1 rounded-full" style={{ background: "rgba(0,216,255,0.15)", color: "#7fe6ff" }}>~{s.zhr}/hr</span>
                      </div>
                      <div className="text-white/45 text-xs mt-1">Peaks {s.peak_date} {s.peak_in_days > 0 ? `· in ${s.peak_in_days} days` : s.peak_in_days === 0 ? "· tonight!" : "· recently"} · radiant in {s.radiant}</div>
                      <p className="text-white/65 text-sm mt-2 leading-relaxed">{s.note}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming */}
            {data.upcoming_showers.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3"><CalendarBlank size={15} weight="duotone" color="#A1A1AA" /><span className="text-tel text-[9px] text-white/50">Coming Soon</span></div>
                <div className="space-y-2">
                  {data.upcoming_showers.map((s) => (
                    <div key={s.name} className="flex items-center justify-between text-sm">
                      <span className="text-white/80">{s.name}</span>
                      <span className="text-white/40 text-xs">peaks {s.peak_date} · in {s.starts_in_days}d</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sun event */}
            {data.next_sun_event && (
              <div>
                <div className="flex items-center gap-2 mb-3"><Sun size={15} weight="duotone" color="#A1A1AA" /><span className="text-tel text-[9px] text-white/50">Next Solar Milestone</span></div>
                <div className="glass rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold text-base tracking-tight">{data.next_sun_event.name}</span>
                    <span className="text-white/40 text-xs">in {data.next_sun_event.in_days}d</span>
                  </div>
                  <div className="text-white/45 text-xs mt-1">{data.next_sun_event.date}</div>
                  <p className="text-white/65 text-sm mt-2 leading-relaxed">{data.next_sun_event.note}</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <div className="px-6 py-3 border-t border-white/10">
        <span className="text-tel text-[8px] text-white/30">Positions computed live for your location · Verified astronomy</span>
      </div>
    </div>
  );
}
